import {
  Accordion,
  AccordionPanel,
  AccordionTrigger,
  Flex,
  Select,
  Text,
} from '@backstage/ui';
import { selectedKeys, toOptions, withSelectedFirst } from '../select-helpers';
import {
  SCOPE_FACETS,
  SCOPE_FACET_LABELS,
  type AnnotationFilter,
  type ScopeFacet,
  type ScopeFacetValues,
} from './scope-filter-model';
import { AnnotationFilterFields } from './annotation-filter-fields';

export type ScopeFacetFieldsProps = {
  /** Distinguishes the include/exclude instances so accordion ids stay unique. */
  mode: 'include' | 'exclude';
  options: Record<ScopeFacet, string[]>;
  values: ScopeFacetValues;
  onChange: (values: ScopeFacetValues) => void;
  /** Annotation values present in the catalog, keyed by annotation key. */
  annotationOptions: Record<string, string[]>;
  annotations: AnnotationFilter[];
  onAnnotationsChange: (values: AnnotationFilter[]) => void;
  isDisabled?: boolean;
};

const DESCRIPTIONS: Record<
  ScopeFacet,
  Record<'include' | 'exclude', string>
> = {
  'spec.lifecycle': {
    include: 'Only entities with these lifecycles are evaluated.',
    exclude: 'Entities with these lifecycles are excluded.',
  },
  'spec.owner': {
    include: 'Only entities owned by these groups are evaluated.',
    exclude: 'Entities owned by these groups are excluded.',
  },
  'metadata.tags': {
    include: 'Only entities carrying these tags are evaluated.',
    exclude: 'Entities carrying these tags are excluded.',
  },
};

const countSelected = (
  values: ScopeFacetValues,
  annotations: AnnotationFilter[],
): number =>
  SCOPE_FACETS.reduce(
    (total, facet) => total + (values[facet]?.length ?? 0),
    annotations.filter(annotation => annotation.key).length,
  );

/**
 * Optional lifecycle/owner/tag/annotation narrowing for a scope section.
 * Selections are ANDed onto every kind rule, so they further restrict the
 * matched entities.
 */
export const ScopeFacetFields = ({
  mode,
  options,
  values,
  onChange,
  annotationOptions,
  annotations,
  onAnnotationsChange,
  isDisabled = false,
}: ScopeFacetFieldsProps) => {
  const selectedCount = countSelected(values, annotations);

  return (
    <Accordion id={`${mode}-facets`}>
      <AccordionTrigger>
        <Flex align="center" gap="2">
          <Text variant="body-small" weight="bold">
            More filters
          </Text>
          {selectedCount > 0 && (
            <Text variant="body-small" color="secondary">
              {selectedCount} selected
            </Text>
          )}
        </Flex>
      </AccordionTrigger>
      <AccordionPanel>
        <Flex direction="column" gap="3" pt="2">
          {SCOPE_FACETS.map(facet => {
            const selected = values[facet] ?? [];
            return (
              <Select
                key={facet}
                label={SCOPE_FACET_LABELS[facet]}
                description={DESCRIPTIONS[facet][mode]}
                selectionMode="multiple"
                search
                options={withSelectedFirst(
                  toOptions(options[facet] ?? []),
                  selected,
                )}
                value={selected}
                isDisabled={isDisabled}
                onChange={keys =>
                  onChange({ ...values, [facet]: selectedKeys(keys) })
                }
              />
            );
          })}
          <AnnotationFilterFields
            mode={mode}
            options={annotationOptions}
            values={annotations}
            onChange={onAnnotationsChange}
            isDisabled={isDisabled}
          />
        </Flex>
      </AccordionPanel>
    </Accordion>
  );
};
